import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, authHeaders } from "@/lib/api";
import { ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Cell, Legend } from "recharts";
import { Heart, TicketCheck, TrendingUp, Euro, BarChart3, Loader2, Users, PieChart, Download, LineChart as LineIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatTile } from "@/components/StatTile";
import { toast } from "sonner";

const BRAND = "#E63946";
const PALETTE = ["#E63946", "#F4A261", "#2A9D8F", "#457B9D", "#E76F51", "#8338EC", "#3A86FF", "#FB5607"];

function BarBlock({ title, icon: Icon, data, emptyLabel }) {
  return (
    <div className="kn-card p-4" data-testid={`stats-chart-${title}`}>
      <p className="font-bold text-slate-800 text-sm flex items-center gap-1.5 mb-3">{Icon && <Icon className="h-4 w-4 text-[#E63946]" />} {title}</p>
      {(!data || data.length === 0) ? (
        <p className="text-sm text-slate-400 py-6 text-center">{emptyLabel || "Nessun dato disponibile"}</p>
      ) : (
        <ResponsiveContainer width="100%" height={Math.max(120, data.length * 40)}>
          <BarChart data={data} layout="vertical" margin={{ left: 8, right: 20, top: 4, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} />
            <YAxis type="category" dataKey="label" width={90} tick={{ fontSize: 11, fill: "#475569" }} />
            <Tooltip cursor={{ fill: "#fef2f2" }} />
            <Bar dataKey="value" radius={[0, 6, 6, 0]}>
              {data.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

export default function VendorStats() {
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [exporting, setExporting] = useState("");

  const loadStats = useCallback((d) => {
    setLoading(true);
    api.get(`/vendor/stats?days=${d}`, { headers: authHeaders("vendor") })
      .then((r) => setS(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { loadStats(days); }, [days, loadStats]);

  const exportCsv = async (entity) => {
    setExporting(entity);
    try {
      const r = await api.get(`/vendor/export/${entity}`, { headers: authHeaders("vendor"), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `koupnow_${entity}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Download avviato");
    } catch (_) { toast.error("Esportazione non riuscita"); }
    finally { setExporting(""); }
  };

  if (loading && !s) return <div className="py-24 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#E63946]" /></div>;
  if (!s) return <div className="py-24 text-center text-slate-400">Statistiche non disponibili.</div>;

  const cards = [
    { label: "Follower", value: s.followers, icon: Heart, color: "text-pink-600 bg-pink-50", to: "/vendor/followers" },
    { label: "Vendite", value: `€ ${Number(s.revenue || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, icon: Euro, color: "text-emerald-600 bg-emerald-50", to: "/vendor/history" },
    { label: "Coupon riscattati", value: s.redeemed, icon: TicketCheck, color: "text-blue-600 bg-blue-50", to: "/vendor/history" },
    { label: "Tasso conversione", value: `${s.conversion_rate}%`, icon: TrendingUp, color: "text-amber-600 bg-amber-50", to: "/vendor/coupon-clients" },
  ];

  return (
    <div className="space-y-6 kn-fade-up max-w-4xl" data-testid="vendor-stats-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 flex items-center gap-2"><BarChart3 className="h-6 w-6 text-[#E63946]" /> Statistiche</h1>
          <p className="text-slate-500">Tutti i numeri del tuo negozio: pubblico, vendite e conversioni.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex rounded-xl border border-slate-200 overflow-hidden" data-testid="stats-period">
            {[30, 90].map((d) => (
              <button key={d} onClick={() => setDays(d)} data-testid={`stats-period-${d}`}
                className={`px-3 h-10 text-sm font-bold ${days === d ? "bg-[#E63946] text-white" : "bg-white text-slate-500"}`}>
                {d} giorni
              </button>
            ))}
          </div>
          <Button variant="outline" onClick={() => exportCsv("customers")} disabled={!!exporting} data-testid="export-customers" className="h-10 rounded-xl font-bold gap-1.5">
            {exporting === "customers" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Clienti
          </Button>
          <Button variant="outline" onClick={() => exportCsv("sales")} disabled={!!exporting} data-testid="export-sales" className="h-10 rounded-xl font-bold gap-1.5">
            {exporting === "sales" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Vendite
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        {cards.map((c) => (
          <StatTile key={c.label} testId={`stats-card-${c.label}`} icon={c.icon} value={c.value} label={c.label} accent={c.color.split(" ")[0]} onClick={() => navigate(c.to)} />
        ))}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <StatTile testId="stats-card-Coupon richiesti" value={s.total_claims} label="Coupon richiesti" onClick={() => navigate("/vendor/coupon-clients")} />
        <StatTile testId="stats-card-Offerte attive" value={s.live_offers} label="Offerte attive" onClick={() => navigate("/vendor/offers")} />
        <StatTile testId="stats-card-Scontrino medio" value={`€ ${Number(s.avg_ticket || 0).toFixed(2)}`} label="Scontrino medio" onClick={() => navigate("/vendor/history")} />
        <StatTile testId="stats-card-Risparmio dato" value={`€ ${Number(s.savings_given || 0).toFixed(2)}`} label="Risparmio dato" onClick={() => navigate("/vendor/history")} />
      </div>

      <div>
        <h2 className="font-extrabold text-lg text-slate-900 mb-3 flex items-center gap-2"><LineIcon className="h-5 w-5 text-[#E63946]" /> Andamento negli ultimi {s.days} giorni</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div className="kn-card p-4" data-testid="stats-trend-followers">
            <p className="font-bold text-slate-800 text-sm mb-3">Crescita follower</p>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={s.series || []} margin={{ left: -10, right: 12, top: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} interval="preserveStartEnd" minTickGap={24} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} />
                <Tooltip />
                <Line type="monotone" dataKey="followers" name="Follower" stroke="#E63946" strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="kn-card p-4" data-testid="stats-trend-coupons">
            <p className="font-bold text-slate-800 text-sm mb-3">Coupon al giorno</p>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={s.series || []} margin={{ left: -10, right: 12, top: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} interval="preserveStartEnd" minTickGap={24} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} />
                <Tooltip cursor={{ fill: "#fef2f2" }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="claims" name="Richiesti" fill="#457B9D" radius={[4, 4, 0, 0]} />
                <Bar dataKey="redeemed" name="Riscattati" fill="#2A9D8F" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div>
        <h2 className="font-extrabold text-lg text-slate-900 mb-3 flex items-center gap-2"><Users className="h-5 w-5 text-[#E63946]" /> Chi sono i tuoi clienti</h2>
        <p className="text-sm text-slate-500 mb-3">Dati aggregati e anonimi su follower e clienti che hanno richiesto i tuoi coupon ({s.audience} persone).</p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <BarBlock title="Età" icon={PieChart} data={s.demographics?.age} />
          <BarBlock title="Genere" icon={Users} data={s.demographics?.gender} />
          <BarBlock title="Città" icon={PieChart} data={s.demographics?.city} />
          <BarBlock title="Paese" icon={PieChart} data={s.demographics?.country} />
        </div>
      </div>

      <div>
        <h2 className="font-extrabold text-lg text-slate-900 mb-3">Offerte più richieste</h2>
        <BarBlock title="Coupon per offerta" icon={TicketCheck}
          data={(s.top_offers || []).map((o) => ({ label: o.title, value: o.claims }))}
          emptyLabel="Nessuna offerta ancora" />
      </div>
    </div>
  );
}
